import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  Mail, 
  Server, 
  Lock, 
  Send, 
  CheckCircle, 
  AlertCircle, 
  Loader2,
  Eye,
  EyeOff,
  Settings,
  Shield,
  Pencil,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SMTPManager() {
  const [settings, setSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    from_email: '',
    from_name: 'MyPatriotMail',
    use_tls: true,
    use_ssl: false,
    is_enabled: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [originalSettings, setOriginalSettings] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/smtp/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const loadedSettings = {
          smtp_host: data.smtp_host || '',
          smtp_port: data.smtp_port || 587,
          smtp_username: data.smtp_username || '',
          smtp_password: '',
          from_email: data.from_email || '',
          from_name: data.from_name || 'MyPatriotMail',
          use_tls: data.use_tls ?? true,
          use_ssl: data.use_ssl ?? false,
          is_enabled: data.is_enabled ?? false
        };
        setSettings(loadedSettings);
        setOriginalSettings(loadedSettings);
        setIsConfigured(data.is_configured);
      }
    } catch (error) {
      console.error('Failed to fetch SMTP settings:', error);
      toast.error('Failed to load SMTP settings');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    if (originalSettings) {
      setSettings(originalSettings);
    }
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!settings.smtp_host || !settings.smtp_username || !settings.from_email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/smtp/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success('SMTP settings saved successfully');
        setIsConfigured(true);
        setOriginalSettings(settings);
        setIsEditing(false); // Lock after save
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Failed to save SMTP settings:', error);
      toast.error('Failed to save SMTP settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }

    setTesting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/smtp/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ test_email: testEmail })
      });

      if (response.ok) {
        toast.success(`Test email sent to ${testEmail}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Failed to test SMTP:', error);
      toast.error('Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 text-[#6D4AFF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Card */}
      <Card className="bg-[#1a1a2e] border-[#2d2d44]">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#6D4AFF]" />
            SMTP Email Configuration
          </CardTitle>
          <CardDescription className="text-gray-400">
            Configure outgoing email settings for invitations and system notifications
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-[#252540] rounded-lg">
            <div className="flex items-center gap-3">
              {settings.is_enabled && isConfigured ? (
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                </div>
              ) : (
                <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-yellow-500" />
                </div>
              )}
              <div>
                <p className="text-white font-medium">
                  {settings.is_enabled && isConfigured ? 'SMTP Configured & Active' : 'SMTP Not Configured'}
                </p>
                <p className="text-sm text-gray-400">
                  {settings.is_enabled && isConfigured 
                    ? `Sending from ${settings.from_email}` 
                    : 'Set up SMTP to enable email sending'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-gray-400 text-sm">Enable</Label>
              <Switch
                checked={settings.is_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, is_enabled: checked })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* SMTP Settings Form */}
      <Card className="bg-[#1a1a2e] border-[#2d2d44]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <Server className="w-5 h-5 text-[#6D4AFF]" />
              Server Settings
            </CardTitle>
            {!isEditing ? (
              <Button
                onClick={() => setIsEditing(true)}
                variant="outline"
                className="border-[#6D4AFF] text-[#6D4AFF] hover:bg-[#6D4AFF] hover:text-white"
                data-testid="smtp-edit-btn"
              >
                <Pencil className="w-4 h-4 mr-2" />
                Edit
              </Button>
            ) : (
              <Button
                onClick={handleCancelEdit}
                variant="ghost"
                className="text-gray-400 hover:text-white"
                data-testid="smtp-cancel-btn"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            )}
          </div>
          {!isEditing && (
            <p className="text-xs text-amber-400 mt-2 flex items-center gap-1">
              <Lock className="w-3 h-3" />
              Settings are locked. Click Edit to make changes.
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Server & Port */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2 space-y-2">
              <Label className="text-gray-300">SMTP Host *</Label>
              <Input
                placeholder="smtp.example.com"
                value={settings.smtp_host}
                onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                className="bg-[#252540] border-[#2d2d44] text-white disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={!isEditing}
                data-testid="smtp-host-input"
              />
              <p className="text-xs text-gray-500">e.g., smtp.gmail.com, smtp.sendgrid.net</p>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Port *</Label>
              <Input
                type="number"
                placeholder="587"
                value={settings.smtp_port}
                onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) || 587 })}
                className="bg-[#252540] border-[#2d2d44] text-white disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={!isEditing}
                data-testid="smtp-port-input"
              />
              <p className="text-xs text-gray-500">Usually 587 (TLS) or 465 (SSL)</p>
            </div>
          </div>

          {/* Authentication */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Username *</Label>
              <Input
                placeholder="your-email@example.com"
                value={settings.smtp_username}
                onChange={(e) => setSettings({ ...settings, smtp_username: e.target.value })}
                className="bg-[#252540] border-[#2d2d44] text-white disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={!isEditing}
                data-testid="smtp-username-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Password *</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={settings.smtp_password}
                  onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
                  className="bg-[#252540] border-[#2d2d44] text-white pr-10 disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={!isEditing}
                  data-testid="smtp-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500">For Gmail, use an App Password</p>
            </div>
          </div>

          {/* From Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-300">From Email *</Label>
              <Input
                type="email"
                placeholder="noreply@mypatriotmail.org"
                value={settings.from_email}
                onChange={(e) => setSettings({ ...settings, from_email: e.target.value })}
                className="bg-[#252540] border-[#2d2d44] text-white disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={!isEditing}
                data-testid="smtp-from-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">From Name</Label>
              <Input
                placeholder="MyPatriotMail"
                value={settings.from_name}
                onChange={(e) => setSettings({ ...settings, from_name: e.target.value })}
                className="bg-[#252540] border-[#2d2d44] text-white disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={!isEditing}
                data-testid="smtp-from-name-input"
              />
            </div>
          </div>

          {/* Security Options */}
          <div className={`p-4 bg-[#252540] rounded-lg space-y-4 ${!isEditing ? 'opacity-60' : ''}`}>
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-4 h-4 text-[#6D4AFF]" />
              <Label className="text-white font-medium">Security Settings</Label>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-gray-300">Use TLS (STARTTLS)</Label>
                <p className="text-xs text-gray-500">Recommended for port 587</p>
              </div>
              <Switch
                checked={settings.use_tls}
                onCheckedChange={(checked) => setSettings({ ...settings, use_tls: checked, use_ssl: checked ? false : settings.use_ssl })}
                disabled={!isEditing}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-gray-300">Use SSL</Label>
                <p className="text-xs text-gray-500">For port 465 (implicit SSL)</p>
              </div>
              <Switch
                checked={settings.use_ssl}
                onCheckedChange={(checked) => setSettings({ ...settings, use_ssl: checked, use_tls: checked ? false : settings.use_tls })}
                disabled={!isEditing}
              />
            </div>
          </div>

          {/* Save Button - Only show when editing */}
          {isEditing && (
            <div className="flex justify-end">
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-[#6D4AFF] hover:bg-[#5B3FD9]"
                data-testid="smtp-save-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Settings className="w-4 h-4 mr-2" />
                    Save Settings
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Test Email */}
      <Card className="bg-[#1a1a2e] border-[#2d2d44]">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Send className="w-5 h-5 text-[#6D4AFF]" />
            Test Connection
          </CardTitle>
          <CardDescription className="text-gray-400">
            Send a test email to verify your SMTP configuration
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <Input
              type="email"
              placeholder="test@example.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="bg-[#252540] border-[#2d2d44] text-white flex-1"
            />
            <Button 
              onClick={handleTest} 
              disabled={testing || !isConfigured}
              variant="outline"
              className="border-[#6D4AFF] text-[#6D4AFF] hover:bg-[#6D4AFF] hover:text-white"
            >
              {testing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Test
                </>
              )}
            </Button>
          </div>
          {!isConfigured && (
            <Alert className="mt-4 bg-yellow-500/10 border-yellow-500/50">
              <AlertCircle className="h-4 w-4 text-yellow-500" />
              <AlertDescription className="text-yellow-200">
                Please save your SMTP settings before testing
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Common SMTP Providers Guide */}
      <Card className="bg-[#1a1a2e] border-[#2d2d44]">
        <CardHeader>
          <CardTitle className="text-white text-sm">Quick Setup Guides</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="p-3 bg-[#252540] rounded-lg">
              <p className="text-white font-medium mb-2">Gmail</p>
              <p className="text-gray-400 text-xs">Host: smtp.gmail.com</p>
              <p className="text-gray-400 text-xs">Port: 587 (TLS)</p>
              <p className="text-gray-400 text-xs">Use App Password</p>
            </div>
            <div className="p-3 bg-[#252540] rounded-lg">
              <p className="text-white font-medium mb-2">SendGrid</p>
              <p className="text-gray-400 text-xs">Host: smtp.sendgrid.net</p>
              <p className="text-gray-400 text-xs">Port: 587 (TLS)</p>
              <p className="text-gray-400 text-xs">User: apikey</p>
            </div>
            <div className="p-3 bg-[#252540] rounded-lg">
              <p className="text-white font-medium mb-2">Amazon SES</p>
              <p className="text-gray-400 text-xs">Host: email-smtp.[region].amazonaws.com</p>
              <p className="text-gray-400 text-xs">Port: 587 (TLS)</p>
              <p className="text-gray-400 text-xs">Use IAM credentials</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
